import { MaterialUnit } from '../enums';
import { parseThicknessInches } from './thickness';

/**
 * How a material's purchase unit turns into money.
 *
 * `material_library.unit` is an unvalidated TEXT column (`z.string()` in the
 * schema), and two vocabularies have been written into it over the life of the
 * app: the legacy `'sq ft'` / `'lin ft'` spellings the calculation code was
 * originally written against, and the `MaterialUnit` enum the Materials
 * Library modal has written ever since it gained a Unit dropdown.
 *
 * Nothing reconciled the two, so a material saved as `linear_foot` matched no
 * branch in the cost helpers and fell through to the per-square-foot default —
 * silently pricing a $3.80/lf board at $3.80/sqft. Normalising here, once, is
 * what stops that: every call site asks this module what a unit means rather
 * than comparing against a literal of its own.
 *
 * Adding a spelling is a one-line change to UNIT_ALIASES. Adding a *pricing
 * rule* is a branch in priceMaterialByUnit and nowhere else.
 */

/**
 * Every spelling seen in the wild, mapped to its canonical MaterialUnit.
 *
 * Keys are compared lowercased and trimmed. Both the enum values and the
 * legacy strings are listed explicitly rather than derived, so that a future
 * rename of an enum value cannot silently orphan the legacy row that still
 * spells it the old way.
 */
const UNIT_ALIASES: Readonly<Record<string, MaterialUnit>> = {
  // Sheet
  sheet: MaterialUnit.SHEET,
  sheets: MaterialUnit.SHEET,
  sht: MaterialUnit.SHEET,

  // Linear foot
  linear_foot: MaterialUnit.LINEAR_FOOT,
  'linear foot': MaterialUnit.LINEAR_FOOT,
  'linear feet': MaterialUnit.LINEAR_FOOT,
  'lin ft': MaterialUnit.LINEAR_FOOT,
  linft: MaterialUnit.LINEAR_FOOT,
  lf: MaterialUnit.LINEAR_FOOT,

  // Square foot
  square_foot: MaterialUnit.SQUARE_FOOT,
  'square foot': MaterialUnit.SQUARE_FOOT,
  'square feet': MaterialUnit.SQUARE_FOOT,
  'sq ft': MaterialUnit.SQUARE_FOOT,
  sqft: MaterialUnit.SQUARE_FOOT,
  sf: MaterialUnit.SQUARE_FOOT,

  // Each
  each: MaterialUnit.EACH,
  ea: MaterialUnit.EACH,
  piece: MaterialUnit.EACH,

  // Roll
  roll: MaterialUnit.ROLL,
  rolls: MaterialUnit.ROLL,

  // Yard
  yard: MaterialUnit.YARD,
  yards: MaterialUnit.YARD,
  yd: MaterialUnit.YARD,

  // Pound
  pound: MaterialUnit.POUND,
  lb: MaterialUnit.POUND,
  lbs: MaterialUnit.POUND,
};

/**
 * Resolve a stored unit string to its canonical MaterialUnit.
 *
 * An absent unit becomes SHEET, which is both the column default and what the
 * cost helpers' `material.unit ?? 'sheet'` already assumed.
 *
 * An unrecognised non-empty string returns `null` rather than a guess. Callers
 * price a `null` the way the code priced an unmatched unit before this module
 * existed — per square foot — so an unknown spelling keeps its old number
 * instead of silently re-pricing against a unit nobody chose.
 */
export function normalizeMaterialUnit(
  unit: string | null | undefined,
): MaterialUnit | null {
  const key = (unit ?? '').trim().toLowerCase();
  if (key === '') return MaterialUnit.SHEET;
  return UNIT_ALIASES[key] ?? null;
}

/**
 * One rectangle cut from a linear-foot material.
 *
 * Linear-foot stock is a board of fixed width and arbitrary length, so pricing
 * it needs the piece's real geometry, not just its area. Area alone treats the
 * board as infinitely divisible: a 4" rail ripped from a 5" board comes out as
 * 0.8 of a board-length, when in truth you consume the whole 48" and throw the
 * spare inch away.
 */
export interface UnitPricedPiece {
  /** Inches. */
  width_in: number;
  /** Inches. */
  length_in: number;
  qty: number;
  /**
   * Run the board along the SHORTER dimension instead of the longer one.
   *
   * Only matters because rips round up to whole boards: with exact division
   * both orientations give the same total. A 42x50 piece off a 5" board is
   * ceil(42/5)=9 boards x 50" = 450" the default way, and ceil(50/5)=10
   * boards x 42" = 420" flipped.
   */
  flipped?: boolean;
}

/**
 * Inches of board consumed by one piece: whole rips x the run length x qty.
 *
 * Returns 0 for a degenerate piece or a board of no width — the caller warns
 * about the missing width, this just declines to invent a number.
 */
export function boardRunInches(piece: UnitPricedPiece, boardWidthIn: number): number {
  const { width_in: w, length_in: l, qty } = piece;
  if (!(w > 0) || !(l > 0) || !(qty > 0) || !(boardWidthIn > 0)) return 0;

  const longer = Math.max(w, l);
  const shorter = Math.min(w, l);
  // `run` goes along the grain of the board; `cover` is the span that has to
  // be made up out of board widths.
  const run = piece.flipped ? shorter : longer;
  const cover = piece.flipped ? longer : shorter;

  return Math.ceil(cover / boardWidthIn) * run * qty;
}

/**
 * A solid block cut from slab stock — a bolster's foam blank. Inches, before
 * waste.
 *
 * Its `thickness_in` is the BLOCK's, and it sets the volume. The material's own
 * thickness is a different number with a different job: it sets the rate. That
 * split is what lets a 3" slab price an 8" bolster.
 */
export interface UnitPricedBlock {
  width_in: number;
  length_in: number;
  thickness_in: number;
}

/** The fields of a material that bear on how its unit converts to money. */
export interface UnitPricedMaterial {
  unit?: string | null;
  cost_per_unit?: number | null;
  /**
   * Inches. Three jobs depending on the unit: the sheet's width for SHEET and
   * ROLL, the board's width for LINEAR_FOOT, and the roll's width for YARD
   * when `roll_width` is not set.
   */
  sheet_width?: number | null;
  /** Inches. Only SHEET and ROLL use it. */
  sheet_length?: number | null;
  /** Inches. Preferred over sheet_width for YARD. */
  roll_width?: number | null;
  /**
   * Free text (`2`, `1-1/2`, `3/4"`), read through parseThicknessInches. Only
   * the board-foot branch reads it, to turn a slab's price into a rate.
   */
  thickness?: string | number | null;
}

export interface MaterialUnitPricingOptions {
  /**
   * `whole_sheet` charges every sheet opened; `used_area` charges the fraction
   * consumed. Only SHEET and ROLL read it.
   */
  costMode?: 'whole_sheet' | 'used_area';
  /**
   * How many discrete pieces are cut from this material. Only EACH reads it,
   * and EACH cannot be priced without it — area carries no piece count.
   *
   * `0` is an answer, not a gap: it means the caller looked and there are no
   * pieces, so nothing is charged and nothing is warned about. Omitting the
   * field means the caller cannot say, which does warn.
   */
  pieceQty?: number;
  /**
   * Pounds of this material consumed. Only POUND reads it, and POUND cannot be
   * priced without it — bulk fill has no stock rectangle and reports no area,
   * so the weight IS the quantity.
   *
   * The same contract as `pieceQty`: `0` is an answer, not a gap, and charges
   * nothing silently; omitting the field means the caller cannot say, which
   * does warn. Unlike `pieceQty` the value is not rounded up — a pound is
   * divisible bulk, not a thing you buy whole.
   */
  weightLb?: number;
  /**
   * The rectangles cut from this material. Only LINEAR_FOOT reads them, and
   * only they can express whole-board ripping. Without them a linear-foot
   * material falls back to area over board width, which under-charges every
   * piece narrower than the board.
   */
  pieces?: readonly UnitPricedPiece[];
  /**
   * A solid block to price by volume. Only SHEET, ROLL and SQUARE_FOOT read
   * it: they are slab stock, so their price per board foot is knowable from
   * the material's size and thickness. Every other unit ignores it and prices
   * exactly as it would without it.
   *
   * `costMode` does not apply here. Board feet are divisible, so this never
   * charges a whole sheet.
   */
  block?: UnitPricedBlock;
  /** Names the material in warnings, so a preview says which row to fix. */
  materialName?: string | null;
}

/** Short label for the quantity a material is actually billed in. */
export type ChargedUnitLabel = 'sht' | 'sqft' | 'lf' | 'yd' | 'pc' | 'lb' | 'bf';

export interface MaterialUnitPrice {
  /** Sheets opened. 0 for units not bought as sheets. */
  num_sheets: number;
  cost: number;
  /** Area of one sheet in sqft. 0 for units not bought as sheets. */
  sheet_sqft: number;
  /**
   * What was billed, in `charged_unit` — the number that divides into `cost`
   * to give the material's own rate. Breakdown rows show this instead of the
   * square footage they used to print for every unit alike, which read as
   * though a linear-foot board were being charged by area.
   */
  charged_qty: number;
  charged_unit: ChargedUnitLabel;
  /** The block's volume before waste. Set only when `block` priced it. */
  board_feet?: number;
  /** The material's price per board foot. Set only when `block` priced it. */
  rate_per_board_foot?: number;
  /**
   * Why a cost came back as 0 despite real area. Empty is the normal case.
   * Callers fold these into the `hardware_warnings` array that already
   * reaches the cost previews and the `note` on template/ex-fabric results.
   */
  warnings: string[];
}

const EMPTY: MaterialUnitPrice = {
  num_sheets: 0, cost: 0, sheet_sqft: 0, charged_qty: 0, charged_unit: 'sqft', warnings: [],
};

/** 12" x 12" x 1". */
const CUBIC_INCHES_PER_BOARD_FOOT = 144;

/** The one wording for "this board has no width", used by both LF paths. */
function missingBoardWidth(name: string | null): string {
  return `${label(name)} is priced per linear foot, but has no board width recorded, so it was not charged. Set its Board Width in the Materials Library.`;
}

/** The one wording for "this sheet has no size", used by both sheet paths. */
function missingSheetSize(name: string | null, unit: MaterialUnit): string {
  return `${label(name)} is priced per ${unit}, but has no sheet size recorded, so it was not charged. Set its width and length in the Materials Library.`;
}

/** `X` when the material has a name, so a warning can point at the row. */
function label(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  return trimmed === '' ? 'A material' : `"${trimmed}"`;
}

/**
 * Price `sqft` of a material according to the unit it is bought in.
 *
 * The single place unit semantics live. Every cost helper delegates here, so
 * the client and server mirrors cannot disagree about what `linear_foot` means
 * the way they disagreed about `lin ft` before.
 *
 * @param sqft        Area consumed, before waste.
 * @param material    The material, or null for "nothing selected".
 * @param wasteFactor Fractional overage, e.g. 0.1 for 10%. Area-based units
 *                    apply it; EACH does not, since pieces are bought whole.
 */
export function priceMaterialByUnit(
  sqft: number,
  material: UnitPricedMaterial | null | undefined,
  wasteFactor = 0.1,
  opts: MaterialUnitPricingOptions = {},
): MaterialUnitPrice {
  if (!material) return { ...EMPTY };

  const unit = normalizeMaterialUnit(material.unit);
  const costPerUnit = material.cost_per_unit ?? 0;
  const { costMode = 'whole_sheet', pieceQty, weightLb, pieces, block, materialName } = opts;
  const name = materialName ?? null;

  // EACH is settled before the area guard: a piece count is real work even
  // when the caller reports no area for it.
  if (unit === MaterialUnit.EACH) {
    if (pieceQty == null || !Number.isFinite(pieceQty) || pieceQty < 0) {
      return {
        ...EMPTY,
        warnings: [
          `${label(name)} is priced per piece, but this calculation has no piece count for it, so it was not charged.`,
        ],
      };
    }
    // An explicit zero is the caller saying there are no pieces. Nothing to
    // charge, and nothing worth warning about.
    if (pieceQty === 0) return { ...EMPTY };
    // Waste is deliberately not applied: you buy whole pieces, and a
    // fractional piece still costs a whole one.
    const wholePieces = Math.ceil(pieceQty);
    return {
      ...EMPTY,
      cost: wholePieces * costPerUnit,
      charged_qty: wholePieces,
      charged_unit: 'pc',
    };
  }

  // POUND is settled beside EACH, before the area guard, and for the same
  // reason turned up one notch: a fill sold by weight characteristically
  // reports NO area at all. Inside the switch below it would fall past
  // `if (!(sqft > 0)) return { ...EMPTY };` and come back as a silent zero
  // instead of the promised warning.
  if (unit === MaterialUnit.POUND) {
    if (weightLb == null || !Number.isFinite(weightLb) || weightLb < 0) {
      return {
        ...EMPTY,
        warnings: [
          `${label(name)} is priced per pound, but this calculation has no weight for it, so it was not charged.`,
        ],
      };
    }
    // An explicit zero is the caller saying there is no weight. Nothing to
    // charge, and nothing worth warning about — the same contract pieceQty has.
    if (weightLb === 0) return { ...EMPTY };
    // Waste applies, unlike EACH: bulk fill is divisible, so 10% over is 10%
    // more pounds rather than one more whole thing. There is no rounding up.
    const lb = weightLb * (1 + wasteFactor);
    return { ...EMPTY, cost: lb * costPerUnit, charged_qty: lb, charged_unit: 'lb' };
  }

  // A block of slab stock is priced by volume. Settled before the area guard,
  // like POUND, because the block carries the quantity: a bolster's face area
  // says nothing about how thick its foam is.
  if (
    block &&
    (unit === MaterialUnit.SHEET || unit === MaterialUnit.ROLL || unit === MaterialUnit.SQUARE_FOOT)
  ) {
    // The SLAB's thickness turns its price into a rate. The block's own
    // thickness, below, is the volume being bought.
    const slabThicknessIn = parseThicknessInches(material.thickness);
    if (slabThicknessIn == null) {
      return {
        ...EMPTY,
        warnings: [
          `${label(name)} is priced by volume here, but its thickness (${JSON.stringify(
            material.thickness ?? '',
          )}) cannot be read, so it was not charged. Set its Thickness in the Materials Library.`,
        ],
      };
    }

    let rate: number;
    if (unit === MaterialUnit.SQUARE_FOOT) {
      // One square foot of a T" slab is T board feet.
      rate = costPerUnit / slabThicknessIn;
    } else {
      const slabBoardFeet =
        ((material.sheet_width ?? 48) * (material.sheet_length ?? 96) * slabThicknessIn) /
        CUBIC_INCHES_PER_BOARD_FOOT;
      if (!(slabBoardFeet > 0)) return { ...EMPTY, warnings: [missingSheetSize(name, unit)] };
      rate = costPerUnit / slabBoardFeet;
    }

    const dims = [block.width_in, block.length_in, block.thickness_in];
    if (dims.some((d) => !Number.isFinite(d) || d < 0)) {
      return {
        ...EMPTY,
        warnings: [
          `${label(name)} was given a foam block with a negative or unreadable size, so it was not charged.`,
        ],
      };
    }
    // A zero dimension is the caller saying there is nothing to cut — the same
    // contract pieceQty and weightLb have.
    if (dims.some((d) => d === 0)) return { ...EMPTY };

    const board_feet =
      (block.width_in * block.length_in * block.thickness_in) / CUBIC_INCHES_PER_BOARD_FOOT;
    // Waste applies, and nothing rounds up: costMode is deliberately ignored,
    // so a small blank never pays for the whole sheet it was cut from.
    const charged = board_feet * (1 + wasteFactor);
    return {
      ...EMPTY,
      cost: charged * rate,
      charged_qty: charged,
      charged_unit: 'bf',
      board_feet,
      rate_per_board_foot: rate,
    };
  }

  // Like EACH above, a linear-foot material priced from piece geometry is
  // settled before the area guard: the pieces carry the quantity, so the sqft
  // argument is redundant for it and may legitimately be 0.
  if (unit === MaterialUnit.LINEAR_FOOT && pieces && pieces.length > 0) {
    const boardWidthIn = material.sheet_width ?? 0;
    if (!(boardWidthIn > 0)) return { ...EMPTY, warnings: [missingBoardWidth(name)] };

    const runInches = pieces.reduce((sum, p) => sum + boardRunInches(p, boardWidthIn), 0);
    const linft = (runInches / 12) * (1 + wasteFactor);
    return { ...EMPTY, cost: linft * costPerUnit, charged_qty: linft, charged_unit: 'lf' };
  }

  if (!(sqft > 0)) return { ...EMPTY };

  const sqftWithWaste = sqft * (1 + wasteFactor);

  switch (unit) {
    case MaterialUnit.SHEET:
    case MaterialUnit.ROLL: {
      const sw = material.sheet_width ?? 48;
      const sl = material.sheet_length ?? 96;
      const sheetSqft = (sw * sl) / 144;
      if (!(sheetSqft > 0)) return { ...EMPTY, warnings: [missingSheetSize(name, unit)] };
      const numSheets = Math.ceil(sqftWithWaste / sheetSqft);
      const cost =
        costMode === 'used_area'
          ? (sqftWithWaste / sheetSqft) * costPerUnit
          : numSheets * costPerUnit;
      return {
        num_sheets: numSheets,
        cost,
        sheet_sqft: sheetSqft,
        // used_area bills a fraction of a sheet, so the charged quantity is
        // the fraction, not the count of sheets opened.
        charged_qty: costMode === 'used_area' ? sqftWithWaste / sheetSqft : numSheets,
        charged_unit: 'sht',
        warnings: [],
      };
    }

    case MaterialUnit.LINEAR_FOOT: {
      // Reached only when the caller supplied no piece geometry — finish
      // slots, foam, dacron. Area over board width equals the whole-board
      // maths above when every piece divides evenly into the board width, and
      // under-charges otherwise, so pass `pieces` wherever they are known.
      const boardWidthIn = material.sheet_width ?? 0;
      if (!(boardWidthIn > 0)) return { ...EMPTY, warnings: [missingBoardWidth(name)] };

      const linft = sqftWithWaste / (boardWidthIn / 12);
      return { ...EMPTY, cost: linft * costPerUnit, charged_qty: linft, charged_unit: 'lf' };
    }

    case MaterialUnit.YARD: {
      // Same conversion as linear foot, off the roll width and in yards.
      const rollWidthIn = material.roll_width ?? material.sheet_width ?? 0;
      if (!(rollWidthIn > 0)) {
        return {
          ...EMPTY,
          warnings: [
            `${label(name)} is priced per yard, but has no roll width recorded, so it was not charged. Set its Roll Width in the Materials Library.`,
          ],
        };
      }
      const yards = ((sqftWithWaste * 144) / rollWidthIn) / 36;
      return { ...EMPTY, cost: yards * costPerUnit, charged_qty: yards, charged_unit: 'yd' };
    }

    case MaterialUnit.SQUARE_FOOT:
    default:
      // `default` covers a null unit — a spelling this module does not know.
      // Pricing it per square foot is exactly what the helpers did before
      // normalisation existed, so an unknown unit keeps its previous number.
      return {
        ...EMPTY,
        cost: sqftWithWaste * costPerUnit,
        charged_qty: sqftWithWaste,
        charged_unit: 'sqft',
      };
  }
}

/**
 * The billed quantity as a breakdown row shows it, e.g. `21.3 sqft`, `2 sht`,
 * `4.4 lf`, `3 pc`.
 *
 * Framing rows used to print square footage for every unit alike, so a board
 * bought by the linear foot advertised an area beside a linear-foot price and
 * read as though it were being charged per square foot.
 */
export function formatChargedQty(qty: number, unit: ChargedUnitLabel): string {
  // Sheets and pieces are things you buy whole; the rest — square feet, linear
  // feet, yards, pounds and board feet — are divisible and read better to 0.1.
  const whole = unit === 'sht' || unit === 'pc';
  const n = whole ? Math.round(qty) : Math.round(qty * 10) / 10;
  return `${n} ${unit}`;
}
